// src/pages/MPFlow/components/EddyChat.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';

/* ── Steps ──────────────────────────────────────── */
const STEPS = [
  { key: 'nicho',       question: 'Qual é o seu nicho?',                         placeholder: 'Ex: Fitness feminino, Culinária saudável, SaaS B2B...' },
  { key: 'diferencial', question: 'Qual é o seu diferencial?',                   placeholder: 'Ex: Método exclusivo de 30 dias com suporte 24h...' },
  { key: 'dor_cliente', question: 'Qual a principal dor do seu cliente?',         placeholder: 'Ex: Falta de tempo para treinar, não consegue emagrecer...' },
];
const GREETING      = 'Oi! Prontos para aumentar suas vendas?';
const FINAL_MESSAGE = 'Perfeito! Vou gerar sua campanha agora.';
const THINKING_MSG  = 'Eddy está analisando...';

const ACCEPTED_IMAGES = '.png,.jpg,.jpeg,.webp';
const ACCEPTED_FILES  = '.pdf,.doc,.docx,.txt';

/* ── TypeWriter ─────────────────────────────────── */
function TypeWriter({ text, onDone, speed = 18 }) {
  const [displayed, setDisplayed] = useState('');
  const doneRef = useRef(false);

  useEffect(() => {
    doneRef.current = false;
    setDisplayed('');
    let i = 0;
    const iv = setInterval(() => {
      i++;
      setDisplayed(text.slice(0, i));
      if (i >= text.length) {
        clearInterval(iv);
        if (!doneRef.current) { doneRef.current = true; onDone?.(); }
      }
    }, speed);
    return () => clearInterval(iv);
  }, [text, onDone, speed]);

  return (
    <span>
      {displayed}
      {displayed.length < text.length && <span className="edy-cursor">|</span>}
    </span>
  );
}

/* ── SVG Icons ──────────────────────────────────── */
const MicIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
    <path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/>
  </svg>
);
const MicStopIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="2"/></svg>
);
const ImageIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
  </svg>
);
const ClipIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
  </svg>
);
const SendIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
  </svg>
);

/* ── EddyAvatar ─────────────────────────────────── */
function EddyAvatar() {
  return (
    <div className="edy-avatar">
      <img src="/robot.png" alt="Eddy" className="edy-avatar__img" />
    </div>
  );
}

/* ── EddySpeechBubble ───────────────────────────── */
function EddySpeechBubble({ text, onDone, thinking }) {
  if (thinking) {
    return (
      <div className="edy-bubble edy-bubble--thinking">
        <span className="edy-bubble__dots"><span/><span/><span/></span>
        <span className="edy-bubble__thinking-text">{THINKING_MSG}</span>
      </div>
    );
  }
  return (
    <div className="edy-bubble">
      <TypeWriter text={text} onDone={onDone} key={text} />
    </div>
  );
}

/* ── EddySmartInput ─────────────────────────────── */
function EddySmartInput({
  value, onChange, onSend, placeholder, disabled,
  recording, onToggleRecord, onImageSelect, onFileSelect,
  imageInputRef, fileInputRef,
  attachment, onRemoveAttachment,
}) {
  const textareaRef = useRef(null);

  // Auto-resize textarea
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 120) + 'px';
  }, [value]);

  // Focus quando habilitado
  useEffect(() => {
    if (!disabled) textareaRef.current?.focus();
  }, [disabled]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  return (
    <div className="edy-smart-input">
      {/* Attachment preview inline */}
      {attachment && (
        <div className="edy-smart-input__att">
          {attachment.type === 'image' && attachment.preview && (
            <img src={attachment.preview} alt="Preview" className="edy-smart-input__att-img" />
          )}
          {attachment.type === 'file' && (
            <span className="edy-smart-input__att-file">{attachment.name}</span>
          )}
          <button className="edy-smart-input__att-x" onClick={onRemoveAttachment} aria-label="Remover">×</button>
        </div>
      )}

      <div className="edy-smart-input__row">
        {/* Mic */}
        <button
          className={`edy-smart-input__btn ${recording ? 'edy-smart-input__btn--rec' : ''}`}
          onClick={onToggleRecord}
          title={recording ? 'Parar gravação' : 'Gravar áudio'}
          aria-label={recording ? 'Parar gravação' : 'Gravar áudio'}
          type="button"
        >
          {recording ? <MicStopIcon /> : <MicIcon />}
        </button>

        {/* Textarea */}
        <textarea
          ref={textareaRef}
          className="edy-smart-input__textarea"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          rows={1}
          maxLength={500}
        />

        {/* Clip */}
        <button className="edy-smart-input__btn" onClick={() => fileInputRef.current?.click()} title="Anexar arquivo" aria-label="Anexar arquivo" type="button">
          <ClipIcon />
        </button>
        <input ref={fileInputRef} type="file" accept={ACCEPTED_FILES} onChange={onFileSelect} hidden />

        {/* Image */}
        <button className="edy-smart-input__btn" onClick={() => imageInputRef.current?.click()} title="Enviar imagem" aria-label="Enviar imagem" type="button">
          <ImageIcon />
        </button>
        <input ref={imageInputRef} type="file" accept={ACCEPTED_IMAGES} onChange={onImageSelect} hidden />

        {/* Send */}
        <button
          className="edy-smart-input__send"
          onClick={onSend}
          disabled={disabled || value.trim().length < 2}
          aria-label="Enviar"
          type="button"
        >
          <SendIcon />
        </button>
      </div>
    </div>
  );
}

/* ── Main: EddyAssistant ────────────────────────── */
export default function EddyChat({ onComplete }) {
  const [stepIndex, setStepIndex]     = useState(0);
  const [answers, setAnswers]         = useState({ nicho: '', diferencial: '', dor_cliente: '' });
  const [input, setInput]             = useState('');
  const [inputEnabled, setInputEnabled] = useState(false);
  const [done, setDone]               = useState(false);
  const [thinking, setThinking]       = useState(false);
  const [bubbleText, setBubbleText]   = useState(GREETING);
  const [recording, setRecording]     = useState(false);
  const [attachment, setAttachment]   = useState(null);
  const imageInputRef = useRef(null);
  const fileInputRef  = useRef(null);
  const recognitionRef = useRef(null);

  // Primeira pergunta aparece após greeting
  useEffect(() => {
    const timer = setTimeout(() => {
      setBubbleText(STEPS[0].question);
    }, 1200);
    return () => clearTimeout(timer);
  }, []);

  const handleTypingDone = useCallback(() => {
    if (!done && !thinking) setInputEnabled(true);
  }, [done, thinking]);

  const advance = useCallback((value) => {
    const currentStep = STEPS[stepIndex];
    const newAnswers = { ...answers, [currentStep.key]: value };
    setAnswers(newAnswers);
    setInputEnabled(false);
    setAttachment(null);

    const nextIndex = stepIndex + 1;

    // Mostrar "pensando"
    setThinking(true);
    setTimeout(() => {
      setThinking(false);
      if (nextIndex < STEPS.length) {
        setStepIndex(nextIndex);
        setBubbleText(STEPS[nextIndex].question);
      } else {
        setDone(true);
        setBubbleText(FINAL_MESSAGE);
        setTimeout(() => onComplete?.(newAnswers), 2000);
      }
    }, 1200);
  }, [stepIndex, answers, onComplete]);

  const handleSend = () => {
    const value = input.trim();
    if (!value || value.length < 2 || !inputEnabled || done || thinking) return;
    advance(value);
    setInput('');
  };

  /* ── Voice ─────────────────────────────────────── */
  const toggleRecording = () => {
    if (recording) {
      recognitionRef.current?.stop();
      setRecording(false);
      return;
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const rec = new SR();
    rec.lang = 'pt-BR';
    rec.interimResults = false;
    rec.onresult = (e) => {
      const t = e.results[0]?.[0]?.transcript || '';
      if (t) setInput(prev => (prev ? prev + ' ' : '') + t);
    };
    rec.onerror = () => setRecording(false);
    rec.onend   = () => setRecording(false);
    recognitionRef.current = rec;
    rec.start();
    setRecording(true);
  };

  /* ── Uploads ───────────────────────────────────── */
  const handleImageSelect = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setAttachment({ type: 'image', name: f.name, preview: URL.createObjectURL(f) });
    e.target.value = '';
  };
  const handleFileSelect = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setAttachment({ type: 'file', name: f.name });
    e.target.value = '';
  };

  const step = STEPS[stepIndex];

  return (
    <div className="edy-assistant">
      {/* Eddy Avatar */}
      <EddyAvatar />

      {/* Speech Bubble */}
      <EddySpeechBubble text={bubbleText} onDone={handleTypingDone} thinking={thinking} />

      {/* Respostas anteriores (compacto) */}
      {Object.entries(answers).filter(([,v]) => v).length > 0 && (
        <div className="edy-assistant__answers">
          {Object.entries(answers).map(([key, val]) =>
            val ? (
              <div key={key} className="edy-assistant__answer-chip">
                <span className="edy-assistant__answer-label">
                  {key === 'nicho' ? 'Nicho' : key === 'diferencial' ? 'Diferencial' : 'Dor'}
                </span>
                {val}
              </div>
            ) : null
          )}
        </div>
      )}

      {/* Smart Input */}
      {!done && (
        <EddySmartInput
          value={input}
          onChange={setInput}
          onSend={handleSend}
          placeholder={step?.placeholder || 'Digite sua resposta ou envie um arquivo...'}
          disabled={!inputEnabled || thinking}
          recording={recording}
          onToggleRecord={toggleRecording}
          onImageSelect={handleImageSelect}
          onFileSelect={handleFileSelect}
          imageInputRef={imageInputRef}
          fileInputRef={fileInputRef}
          attachment={attachment}
          onRemoveAttachment={() => setAttachment(null)}
        />
      )}
    </div>
  );
}
